import { HoverCardWithLink } from '@/components/hover-card-with-link';
import { MarkdownText } from '@/components/markdown-text';
import { NavButton } from '@/components/nav-button';
import { RoundedBadge } from '@/components/rounded-badge';
import { Task } from '@/types';
import { Box, Card, Flex, Heading, Icon, Image, Stack, Text, VStack } from '@chakra-ui/react';
import { FaCircle } from 'react-icons/fa';
import { IoMdCheckmarkCircle } from 'react-icons/io';

export const TaskCard = ({ task, isAuthenticated, idx }: { task: Task; isAuthenticated: boolean; idx: number }) => {
  const isExternalTaskLink = task.targetUrl?.startsWith('http');

  return (
    <Box w="full">
      <Box bgColor={task.completed ? 'brandBlue' : '#F0F0F0'} borderRadius="2xl" padding={[4, 4, 6]}>
        <Flex justifyContent={'space-between'} mb={6} alignItems="center" flexDirection="row">
          <Heading
            size={['xl', 'xl', '2xl']}
            fontWeight="bold"
            display="flex"
            alignItems={['flex-start', 'flex-start', 'center']}
            gap={[1, 1, 2]}
            flexDirection={['column', 'column', 'row']}
          >
            <Box as="span" color="text.secondary" whiteSpace="nowrap" hideBelow="md">
              Step {idx + 1}
            </Box>
            <Box
              as="span"
              color="text.secondary"
              whiteSpace="nowrap"
              hideFrom="md"
              fontSize="14px"
              fontFamily="Inter, sans-serif"
              lineHeight="20px"
              fontWeight="500"
            >
              Step {idx + 1}
            </Box>
            {task.header}
          </Heading>
          {isAuthenticated && (
            <RoundedBadge bgColor="transparent" size="lg" h={10} color={task.completed ? 'bg' : 'fg.muted'}>
              <Text hideBelow="lg" mr={1} color={task.completed ? 'fg' : 'fg.subtle'} fontWeight="500" fontSize="s">
                {task.completed ? 'Completed' : 'Not Completed'}
              </Text>
              {task.completed ? (
                <Icon color="blue.500">
                  <IoMdCheckmarkCircle size={24} />
                </Icon>
              ) : (
                <Icon color="rgba(152, 152, 152, 0.50)">
                  <FaCircle size={20} />
                </Icon>
              )}
            </RoundedBadge>
          )}
        </Flex>
        <HoverCardWithLink
          href={task.targetUrl}
          cardRootStyles={{ bgColor: 'bg', borderColor: 'bg', borderRadius: '2xl' }}
          showHoverIcon={!isExternalTaskLink}
        >
          <Card.Body p={6}>
            <Stack
              justifyContent={'space-between'}
              alignItems={['flex-start', 'flex-start', 'center']}
              flexDirection={['column', 'column', 'row']}
            >
              <Stack gap={4} pr={6} flexDirection={['column', 'column', 'row']}>
                <Image src={task.iconUrl} h={10} w={10} flexShrink={0} />
                <VStack gap={2} alignItems="flex-start">
                  <Heading fontSize="lg" fontWeight="500" lineHeight="24px">
                    {task.title}
                  </Heading>
                  <MarkdownText>{task.description}</MarkdownText>
                </VStack>
              </Stack>
              {task.targetUrl && (
                <NavButton
                  direction="fwd"
                  target="_blank"
                  href={task.targetUrl}
                  fontSize="sm"
                  bgColor="white"
                  fontFamily="Inter, sans-serif"
                  w={['full', 'full', 'auto']}
                >
                  {task.buttonText}
                </NavButton>
              )}
            </Stack>
          </Card.Body>
        </HoverCardWithLink>
      </Box>
    </Box>
  );
};
